import React from 'react'
import { Department } from 'enums/department'

import { TextField, CircularProgress, InputAdornment } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'

import { debounce } from 'lodash-es'
import API from '@aws-amplify/api'
import { IBrand, Brand } from 'interfaces/IBrand'
import SearchIcon from '@mui/icons-material/Search'

let brands: IBrand[] = []
let promise: Promise<void>

export default function LoadBrandAutocomplete(props: {
  name: string
  employeeId?: string
  noRestriction?: boolean | number
  preload?: boolean
  onChange?: (brand: IBrand) => void
  disabled?: boolean
  required?: boolean
  showInactive?: boolean
  department?: Department
}) {
  const {
    onChange,
    name,
    employeeId,
    noRestriction,
    preload,
    disabled,
    showInactive,
    department,
  } = props
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [options, setOptions] = React.useState<Array<string>>(
    name ? [name] : []
  )
  const [tempText, setTempText] = React.useState('')

  const updateOptions = (responseData: IBrand[]) => {
    setOptions(responseData.map((brand) => brand.brandId))
    brands = responseData
  }

  const loadAutocomplete = React.useCallback((tempName: string, employeeId?: string) => {
    if (preload || tempName) {
      setIsLoading(true)
      if (employeeId) {
        const param = {
          queryStringParameters: {
            employeeId,
            ...(showInactive && {
              showInactive: true,
            }),
            department,
          },
        }
        API.get(
          'products',
          `/brands/getBrandsFromEmployeeId?employeeId=${employeeId}&brandId=${tempName}`,
          param
        )
          .then((response) => {
            if (Array.isArray(response) && response.length) {
              updateOptions(response)
            } else {
            }
          })
          .finally(() => {
            setIsLoading(false)
          })
      } else {
        const param = {
          queryStringParameters: {
            fuzzy: tempName ? 'true' : 'false',
            pageSize: 20,
            ...(noRestriction !== undefined && { noRestrictions: noRestriction }),
            ...(showInactive && {
              showInactive: true,
            }),
            department,
          },
        }
        if (promise) {
          API.cancel(promise)
        }
        promise = API.get('products', `/brands/getBrand/${tempName}`, param)
          .then((response) => {
            if (Array.isArray(response) && response.length) {
              updateOptions(response)
            } else {
            }
          })
          .finally(() => {
            setIsLoading(false)
          })
      }
    } else {
    }
  }, [preload, showInactive, noRestriction, department])

  const throttledLoadAutocomplete = React.useMemo(
    () => debounce(loadAutocomplete, 250),
    [loadAutocomplete]
  )
  React.useEffect(
    () => throttledLoadAutocomplete(tempText, employeeId),
    [tempText, employeeId, throttledLoadAutocomplete]
  )
  React.useEffect(() => {
    if (props.preload && options.length === 0) {
      loadAutocomplete('', employeeId) // Pass employeeId here too
    } else if (options.length === 0 && name) {
      setOptions([name])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, name, props.preload])

  return (
    <Autocomplete
      disabled={disabled}
      fullWidth
      onClose={() => {
        setTempText('')
      }}
      onChange={(e: React.ChangeEvent<{}>, selectedBrandId: string | null) => {
        const brand =
          brands.find((brand) => brand.brandId === selectedBrandId) ||
          new Brand()
        if (onChange) onChange(brand)
        setTempText('')
      }}
      value={name}
      options={options}
      onInputChange={(_, newInputValue) => {
        setTempText(newInputValue)
      }}
      inputValue={tempText || name}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          required={props.required}
          {...params}
          label="Merek"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  )
}
